import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Divider, Grid, Stack, Typography, styled, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "Components/_Shared/Buttons/Button";
import { ReactComponent as ArrowDownIcon } from "Components/_Shared/Icons/Assets/arrow-down.svg";
import { Popper } from "Components/_Shared/Popper";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { DatePicker } from "Forms/Components/DatePicker";
import { FormCharacterCounter } from "Forms/Components/FormCharacterCounter";
import { FormDropdown, type Option } from "Forms/Components/FormDropdown";
import { StyledLabelAdornment } from "Forms/Components/FormStyles";
import { type MovableContractFormType, getMovableContractFormSchema } from "Forms/Schemas/Assets/movableContractFormSchema";
import {
  getListMovableAssetContractsQueryKey,
  useCreateOtherMovableAssetContract,
  useDeleteAssetContract,
  useGetOtherMovableAssetContract,
  useListAnalyzerPartners,
  useListAssetContractExpenseOptions,
  useUpdateOtherMovableAssetContract,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  AmountFrequency,
  ListAnalyzerPartnersToolType,
  LoadValuesOptionExpenseType,
  type ProposedSolutionStep,
} from "Infrastructure/ApiZfp/Schema";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { getAmountFrequencyTranslation } from "Infrastructure/Utils/LanguageUtils";
import { forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { NOTE_MAX_LENGTH } from "../constants";
import type { SubmitContractFormHandle } from "./ContractDrawer";

type Props = {
  readonly assetContractId?: string;
  readonly assetItemId: string;
  readonly onClose: () => void;
  readonly onPendingChange?: (pending: boolean) => void;
};

const StyledItem = styled(Stack)`
  width: 300px;
  &:hover {
    cursor: pointer;
    background: rgba(0, 0, 0, 0.03);
  }
`;

export const MovableContractOtherForm = forwardRef<SubmitContractFormHandle, Props>(
  ({ assetItemId, assetContractId, onClose, onPendingChange }, ref) => {
    const queryClient = useQueryClient();
    const theme = useTheme();
    const { readOnly, analysisId, versionId } = useContext(AnalysisDetailContext);
    const { t } = useResource();

    const [expenseOptionsDropdownOpen, setExpenseOptionsDropdownOpen] = useState(false);

    const { data: insurancePartnersData } = useListAnalyzerPartners({ toolType: ListAnalyzerPartnersToolType.insurance });
    const { data: expenseOptions } = useListAssetContractExpenseOptions(analysisId, versionId);
    const { data: movableContract } = useGetOtherMovableAssetContract(analysisId, versionId, assetItemId, assetContractId ?? "", {
      query: { enabled: !!assetContractId },
    });

    const { mutateAsync: createAsync, isPending: isCreating } = useCreateOtherMovableAssetContract();
    const { mutateAsync: editAsync, isPending: isEditing } = useUpdateOtherMovableAssetContract();
    const { mutateAsync: deleteAsync, isPending: isDeleting } = useDeleteAssetContract();

    const isMutating = isCreating || isEditing || isDeleting;

    useEffect(() => {
      onPendingChange?.(isMutating);
    }, [isMutating, onPendingChange]);

    const defaultValues = useMemo(
      () => ({
        frequency: movableContract?.insuranceAmount?.frequency ?? AmountFrequency.yearly,
        newFrequency: movableContract?.proposedSolution?.insuranceAmount?.frequency ?? AmountFrequency.yearly,
        proposedSolutionStepType: movableContract?.proposedSolution?.step ?? "keep",
        partnerId: movableContract?.partnerId?.toString(),
        newPartnerId: movableContract?.proposedSolution?.partnerId?.toString() ?? undefined,
        contractAnniversaryDate: movableContract?.contractAnniversaryDate ?? undefined,
        insuranceAmount: movableContract?.insuranceAmount?.value.toString() ?? ("0" as any),
        newInsuranceAmount: movableContract?.proposedSolution?.insuranceAmount?.value.toString() ?? ("0" as any),
        note: movableContract?.note ?? undefined,
      }),
      [movableContract],
    );

    const methods = useForm<MovableContractFormType>({
      resolver: yupResolver(getMovableContractFormSchema(t)),
      mode: "onBlur",
      defaultValues,
    });

    const {
      control,
      handleSubmit,
      reset,
      setValue,
      watch,
      formState: { errors },
    } = methods;

    useImperativeHandle(ref, () => ({
      submitForm: () => {
        if (!!assetContractId) {
          handleSubmit(onEdit)();
        } else {
          handleSubmit(onCreate)();
        }
      },
      delete: onDelete,
    }));

    const frequencies: Option[] = [
      { value: AmountFrequency.one_time, name: t(Resources.Common.OneTime) },
      { value: AmountFrequency.monthly, name: t(Resources.Common.Monthly) },
      { value: AmountFrequency.quarterly, name: t(Resources.Common.Quarterly) },
      { value: AmountFrequency.half_yearly, name: t(Resources.Common.HalfYearly) },
      { value: AmountFrequency.yearly, name: t(Resources.Common.Yearly) },
    ];

    const proposedSolutionStepTypes: Option[] = [
      { value: "keep", name: t(Resources.AnalysisDetail.AssetsAndContracts.ProposedSolutionStepTypes.Keep) },
      { value: "create_recommended", name: t(Resources.AnalysisDetail.AssetsAndContracts.ProposedSolutionStepTypes.CreateRecommended) },
      { value: "modify", name: t(Resources.AnalysisDetail.AssetsAndContracts.ProposedSolutionStepTypes.Modify) },
    ];

    const insurancePartners: Option[] =
      insurancePartnersData?.map((partner) => ({ value: partner.id.toString(), name: partner.name })) || [];

    const filteredExpenseOptions = expenseOptions?.filter((option) => option.expenseType === LoadValuesOptionExpenseType.other) ?? [];

    const buildAmount = (value: number | undefined, frequency: string) => ({
      value: value || 0,
      currencyCode: "CZK",
      frequency: frequency as AmountFrequency,
    });

    const buildBody = (data: MovableContractFormType) => ({
      partnerId: Number(data.partnerId),
      contractAnniversaryDate: data.contractAnniversaryDate || undefined,
      insuranceAmount: buildAmount(data.insuranceAmount, data.frequency),
      proposedSolution: {
        step: data.proposedSolutionStepType as ProposedSolutionStep,
        partnerId: data.newPartnerId ? Number(data.newPartnerId) : undefined,
        insuranceAmount: buildAmount(data.newInsuranceAmount, data.newFrequency),
      },
      note: data.note || "",
    });

    const invalidate = () => {
      queryClient.invalidateQueries({ queryKey: getListMovableAssetContractsQueryKey(analysisId, versionId, assetItemId) });
    };

    const onCreate = async (data: MovableContractFormType) => {
      try {
        await createAsync({ analysisId, versionId, itemId: assetItemId, data: buildBody(data) });

        invalidate();
        toast.success(t(Resources.AnalysisDetail.AssetsAndContracts.Form.ContractCreateSuccess));
        onClose();
      } catch {
        toast.error(t(Resources.Common.Error));
      }
    };

    const onEdit = async (data: MovableContractFormType) => {
      try {
        await editAsync({ analysisId, versionId, itemId: assetItemId, contractId: assetContractId || "", data: buildBody(data) });

        invalidate();
        toast.success(t(Resources.AnalysisDetail.AssetsAndContracts.Form.ContractEditSuccess));
        onClose();
      } catch {
        toast.error(t(Resources.Common.Error));
      }
    };

    const onDelete = async () => {
      try {
        await deleteAsync({ analysisId, versionId, itemId: assetItemId, contractId: assetContractId || "" });

        invalidate();
        toast.success(t(Resources.AnalysisDetail.AssetsAndContracts.Form.ContractDeleteSuccess));
        onClose();
      } catch {
        toast.error(t(Resources.Common.Error));
      }
    };

    useEffect(() => {
      if (movableContract) {
        reset(defaultValues);
      }
    }, [movableContract, reset, defaultValues]);

    const partnerId = watch("partnerId");
    const insuranceAmount = watch("insuranceAmount");
    const frequency = watch("frequency");
    const proposedSolutionStepType = watch("proposedSolutionStepType");

    return (
      <FormProvider {...methods}>
        <form>
          <Box>
            <Stack spacing={Spacing.XXL}>
              <Grid container spacing={Spacing.XL}>
                {filteredExpenseOptions.length > 0 && (
                  <Grid size={6}>
                    <Popper
                      placement="bottom-start"
                      isOpen={expenseOptionsDropdownOpen}
                      onOpen={() => {
                        setExpenseOptionsDropdownOpen(true);
                      }}
                      btnEl={
                        <Button
                          variant="text"
                          disabled={readOnly}
                          colorCustom={theme.colors.brandPrimary}
                          sx={{ width: "fit-content" }}
                          endAdornment={<ArrowDownIcon />}
                        >
                          {t(Resources.AnalysisDetail.AssetsAndContracts.Form.LoadFromExistingExpenses)}
                        </Button>
                      }
                    >
                      <Stack spacing={Spacing.M} paddingY={Spacing.S} display="flex" justifyContent="space-between">
                        {filteredExpenseOptions.map((option, index) => (
                          <StyledItem
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              justifyContent: "space-between",
                              alignItems: "center",
                              paddingX: Spacing.M,
                              paddingY: Spacing.S,
                              borderRadius: Radius.S,
                            }}
                            key={`${option.expenseType}-${index}`}
                            onClick={() => {
                              setValue("insuranceAmount", option.amount?.value.toString() as any);
                              if (option.amount?.frequency) setValue("frequency", option.amount.frequency);
                              setExpenseOptionsDropdownOpen(false);
                            }}
                          >
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.InsuranceExpenseTypes.Other)}
                              </Typography>
                              <Typography variant="caption" color="secondary" fontWeight={500} mb={Spacing.M}>
                                {option.institution}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="secondary" fontWeight={500}>
                              {formatCurrency(option.amount?.value, option.amount?.currencyCode)}/
                              {option.amount?.frequency ? getAmountFrequencyTranslation(option.amount.frequency, t) : ""}
                            </Typography>
                          </StyledItem>
                        ))}
                      </Stack>
                    </Popper>
                  </Grid>
                )}
                <Grid size={8}>
                  <FormDropdown
                    name="partnerId"
                    options={insurancePartners}
                    control={control}
                    disabled={readOnly}
                    label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.Partner)}
                    labelEndAdornment={<StyledLabelAdornment />}
                    errors={errors}
                  />
                </Grid>
                <Grid size={4}>
                  <DatePicker
                    control={control}
                    disabled={readOnly}
                    name="contractAnniversaryDate"
                    label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.ContractAnniversaryDate)}
                    errors={errors}
                  />
                </Grid>
                <Grid size={6}>
                  <BlFormInput
                    disabled={readOnly}
                    control={control}
                    name="insuranceAmount"
                    label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.InsuranceAmount)}
                    errors={errors}
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 0,
                        min: 0,
                      },
                    ]}
                    inputEndAdornment={
                      <Typography variant="body2" color="secondary">
                        Kč
                      </Typography>
                    }
                  />
                </Grid>
                <Grid size={6}>
                  <FormDropdown
                    name="frequency"
                    options={frequencies}
                    control={control}
                    disabled={readOnly}
                    label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.Frequency)}
                    errors={errors}
                  />
                </Grid>
              </Grid>
              <Divider />
              <Grid container spacing={Spacing.XL}>
                <Grid size={12}>
                  <Typography fontWeight={600} variant="subtitle1">
                    {t(Resources.AnalysisDetail.AssetsAndContracts.Form.ProposedSolutionSteps)}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container spacing={Spacing.XL}>
                <Grid size={6}>
                  <FormDropdown
                    name="proposedSolutionStepType"
                    options={proposedSolutionStepTypes}
                    control={control}
                    disabled={readOnly}
                    label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.ProposedSolution)}
                    errors={errors}
                    onChange={(value) => {
                      if (value === "modify") {
                        if (partnerId) setValue("newPartnerId", partnerId);
                        if (insuranceAmount) setValue("newInsuranceAmount", insuranceAmount);
                        if (frequency) setValue("newFrequency", frequency);
                      }
                    }}
                  />
                </Grid>
              </Grid>
              {proposedSolutionStepType !== "keep" && (
                <Grid container spacing={Spacing.XL}>
                  <Grid size={6}>
                    <FormDropdown
                      name="newPartnerId"
                      options={insurancePartners}
                      control={control}
                      disabled={readOnly}
                      label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.NewPartner)}
                      labelEndAdornment={<StyledLabelAdornment />}
                      errors={errors}
                    />
                  </Grid>
                  <Grid size={3}>
                    <BlFormInput
                      disabled={readOnly}
                      control={control}
                      name="newInsuranceAmount"
                      label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.InsuranceAmount)}
                      labelEndAdornment={<StyledLabelAdornment />}
                      errors={errors}
                      mask={[
                        {
                          mask: Number,
                          thousandsSeparator: " ",
                          scale: 0,
                          min: 0,
                        },
                      ]}
                      inputEndAdornment={
                        <Typography variant="body2" color="secondary">
                          Kč
                        </Typography>
                      }
                    />
                  </Grid>
                  <Grid size={3}>
                    <FormDropdown
                      name="newFrequency"
                      options={frequencies}
                      control={control}
                      disabled={readOnly}
                      label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.Frequency)}
                      labelEndAdornment={<StyledLabelAdornment />}
                      errors={errors}
                    />
                  </Grid>
                </Grid>
              )}
              <Grid size={12}>
                <BlFormInput
                  control={control}
                  label={t(Resources.Common.Note)}
                  name="note"
                  errors={errors}
                  disabled={readOnly}
                  multiline
                  minRows={6}
                  maxRows={8}
                  characterCounter={<FormCharacterCounter characterLimit={NOTE_MAX_LENGTH} name={"note"} />}
                />
              </Grid>
            </Stack>
          </Box>
        </form>
      </FormProvider>
    );
  },
);
